
"use client";

import { motion } from "framer-motion";
import {
  Twitter,
  ArrowUpRight,
  FileText,
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: "Infrastructure",
      links: [
        { name: "Architecture", href: "#architecture" },
        { name: "Protocol", href: "#protocol" },
        { name: "Security", href: "#security" },
        { name: "Governance", href: "#governance" },
      ],
    },
    {
      title: "Workspace",
      links: [
        { name: "AI Studio", href: "#ai" },
        { name: "Payment Console", href: "#payment" },
        { name: "Developer SDK", href: "#dev" },
        { name: "Integrations", href: "#integrations" },
      ],
    },
    {
      title: "Company",
      links: [
        {
          name: "About Gao",
          href: "https://gao.systems/en",
          isExternal: true,
        },
        { name: "Roadmap", href: "#roadmap" },
        {
          name: "Contact",
          href: "mailto:support@gao.systems",
        },
      ],
    },
  ];

  return (
    <footer className="relative bg-[#F8F9FF] text-[#1A1A1A] pt-28 pb-16 overflow-hidden">
      
      {/* Top gradient fade */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent pointer-events-none" />

      {/* Soft animated light */}
      <motion.div
        animate={{ x: ["-10%", "10%", "-10%"] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1200px] h-[500px] bg-blue-500/[0.04] blur-[180px] rounded-full pointer-events-none"
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 mb-28">
          
          {/* Brand Column */}
          <div className="lg:col-span-5 space-y-8">
            <a href="/" className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-all duration-500 group-hover:shadow-[0_0_50px_rgba(59,130,246,0.6)]">
                <img
                  src="/images/dm-logo.png"
                  alt="Logo"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
              <div>
                <div className="font-black text-2xl tracking-tight text-slate-900">
                  GAO
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-blue-600">
                  Workspace
                </div>
              </div>
            </a>

            <p className="text-slate-500 text-sm leading-relaxed max-w-sm">
            Gao Workspace is infrastructure. It provides structured operational control at the domain level — enabling organizations to coordinate, monetize, and automate within a unified and governed digital system.

            </p>
          </div>

          {/* Links */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-14">
            {footerLinks.map((section, idx) => (
              <div key={idx} className="space-y-6">
                <h4 className="text-[10px] font-mono font-bold uppercase tracking-[0.4em] text-blue-600">
                  {section.title}
                </h4>

                <ul className="space-y-4">
                  {section.links.map((link, i) => (
                    <li key={i}>
                      <a
                        href={link.href}
                        target={link.isExternal ? "_blank" : undefined}
                        rel={link.isExternal ? "noopener noreferrer" : undefined}
                        className="relative text-slate-500 hover:text-slate-900 text-sm transition-colors duration-300 group"
                      >
                        {link.name}

                        {/* animated underline */}
                        <span className="absolute left-0 -bottom-1 h-[1px] w-0 bg-blue-600 transition-all duration-300 group-hover:w-full" />

                        {link.isExternal && (
                          <ArrowUpRight
                            size={12}
                            className="inline ml-1 opacity-50"
                          />
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-blue-200 to-transparent mb-12" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">

          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
          Copyright © {currentYear} Gao Internet / Toii Labs LLC.
          </div>

          {/* Social */}
          <div className="flex items-center gap-8">
            {[
              { Icon: Twitter, href: "https://x.com/gao_internet?s=21" },
              { Icon: FileText, href: "https://toii-network.gitbook.io/gao-internet" },
            ].map((social, i) => (
              <motion.a
                key={i}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{
                  y: -4,
                  scale: 1.1,
                }}
                className="text-slate-500 hover:text-blue-600 transition-colors duration-300"
              >
                <social.Icon size={18} strokeWidth={1.5} />
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;